import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";

/*
  U14 — this tab now shows only the enquiries submitted through THIS
  business's own website ("Contact Supplier" form → business_enquiry,
  scoped by domain), via the dedicated get_business_enquiries.php.
  It previously called get_service_enquiries.php, the *global* admin
  feed of homepage category-tile leads — every business owner was
  seeing every business's enquiries. It now refetches whenever the
  owner switches business (listinguid changes).
  Reply/status workflow added via reply_business_enquiry.php — mirrors
  reply_service_enquiry.php but checks ownership (business_site.personaluid)
  instead of an admin token, since this runs in the owner's own dashboard.
*/
const STATUS_STYLE = {
    pending: { bg: "#fff7e6", color: "#b45309", label: "Pending" },
    replied: { bg: "#e8f7ee", color: "#15803d", label: "Replied" },
    closed:  { bg: "#f1f2f4", color: "#4b5563", label: "Closed" },
};

export default function AdminServiceInquiries({ listinguid, personaluid }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [openReplyId, setOpenReplyId] = useState(null);
    const [replyDrafts, setReplyDrafts] = useState({});
    const [sendingId, setSendingId] = useState(null);

    useEffect(() => {
        if (!listinguid) { setRows([]); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        fetch(`${API_BASE}/api/get_business_enquiries.php?listinguid=${encodeURIComponent(listinguid)}`)
            .then((r) => r.json())
            .then((data) => { if (!cancelled && data.success) setRows(data.enquiries || []); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [listinguid]);

    const filteredRows = rows.filter((row) => {
        const query = search.toLowerCase();
        return [row.product, row.name, row.mobile, row.email, row.message]
            .some((value) => String(value || "").toLowerCase().includes(query));
    });
    const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const pageRows = filteredRows.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
    }, [currentPage, totalPages]);

    const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : "?");

    const toggleReply = (row) => {
        setOpenReplyId((current) => (current === row.id ? null : row.id));
        setReplyDrafts((drafts) => (drafts[row.id] !== undefined ? drafts : { ...drafts, [row.id]: row.admin_reply || "" }));
    };

    const sendReply = async (row, status) => {
        const reply = (replyDrafts[row.id] ?? row.admin_reply ?? "").trim();
        if (status === "replied" && reply === "") return;
        setSendingId(row.id);
        try {
            const res = await fetch(`${API_BASE}/api/reply_business_enquiry.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: row.id, personaluid, admin_reply: reply, status }),
            });
            const data = await res.json();
            if (data.success) {
                setRows((current) => current.map((r) => (r.id === row.id ? { ...r, admin_reply: reply, status, replied_at: new Date().toISOString() } : r)));
                setOpenReplyId(null);
            } else {
                alert(data.message || "Could not save the reply.");
            }
        } catch {
            alert("Could not save the reply — please try again.");
        } finally {
            setSendingId(null);
        }
    };

    return (
        <div className="service-inquiries-page">
            <style>{`
                .service-inquiries-page { color: #0b1220; padding: 4px; }
                .service-inquiries-page .dn-hero { display:flex; justify-content:space-between; align-items:center; gap:12px; background:linear-gradient(135deg,#e8f0ff,#fefefe); border-left:6px solid #4a90e2; border-radius:14px; padding:18px 22px; margin-bottom:18px; box-shadow:0 4px 14px rgba(0,0,0,.05); }
                .service-inquiries-page .dn-hero h1 { font-size:1.2rem; }
                .service-inquiries-page .dn-hero p { color:#6b7280; font-size:.9rem; }
                .service-inquiries-page .dn-count { text-align:center; background:#eef3ff; border-radius:12px; padding:8px 18px; }
                .service-inquiries-page .dn-count strong { display:block; font-size:1.6rem; line-height:1; color:#2563eb; }
                .service-inquiries-page .dn-count span { font-size:.72rem; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }
                .service-inquiries-page .dn-card { background:#fff; border-radius:16px; box-shadow:0 6px 20px rgba(0,0,0,.06); overflow:hidden; }
                .service-inquiries-page .dn-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:16px 18px; border-bottom:1px solid #f0f2f5; flex-wrap:wrap; }
                .service-inquiries-page .dn-select { border:1px solid #dfe3e8; border-radius:8px; padding:6px 10px; font-size:14px; background:#fff; }
                .service-inquiries-page .dn-search { display:flex; align-items:center; gap:8px; border:1px solid #dfe3e8; border-radius:24px; padding:7px 14px; min-width:240px; }
                .service-inquiries-page .dn-search i { color:#9aa1a9; }
                .service-inquiries-page .dn-search input { border:0; outline:0; font-size:14px; width:100%; background:transparent; }
                .service-inquiries-page .dn-table { width:100%; border-collapse:separate; border-spacing:0; }
                .service-inquiries-page .dn-table th { background:#f7f9fc; color:#6b7280; font-size:12px; text-transform:uppercase; letter-spacing:.04em; padding:12px 16px; border-bottom:1px solid #eef0f3; white-space:nowrap; }
                .service-inquiries-page .dn-table td { padding:14px 16px; border-bottom:1px solid #f3f5f8; vertical-align:middle; font-size:14px; }
                .service-inquiries-page .dn-table tbody tr:hover { background:#f7faff; }
                .service-inquiries-page .dn-avatar { width:40px; height:40px; flex:0 0 40px; border-radius:10px; background:linear-gradient(135deg,#4a90e2,#5fb3ff); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
                .service-inquiries-page .inquiry-category { background:#eef4ff; color:#2563eb; border:1px solid #dbe7ff; }
                .service-inquiries-page .dn-foot { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:14px 18px; flex-wrap:wrap; }
                .service-inquiries-page .dn-pager { display:flex; gap:6px; }
                .service-inquiries-page .dn-pager button { min-width:36px; height:36px; border:1px solid #e3e7ee; background:#fff; border-radius:9px; color:#2563eb; font-weight:600; cursor:pointer; }
                .service-inquiries-page .dn-pager button.active { background:#2563eb; color:#fff; }
                .service-inquiries-page .dn-pager button:disabled { opacity:.45; cursor:not-allowed; }
                @media (max-width:640px) { .service-inquiries-page .dn-search { min-width:100%; } .service-inquiries-page .dn-hero { align-items:flex-start; flex-direction:column; } }
            `}</style>
            <div className="dn-hero">
                <div>
                    <h1 className="fw-bold mb-1"><i className="bi bi-headset me-2 text-primary"></i>Service Inquiries</h1>
                    <p className="mb-0">
                        Enquiries submitted through this business's own website (the "Contact Supplier" form).
                    </p>
                </div>
                <div className="dn-count"><strong>{rows.length}</strong><span>Total</span></div>
            </div>

            <div className="dn-card">
                <div className="dn-toolbar">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">Show</span>
                        <select className="dn-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            {[10, 50, 100].map((count) => <option key={count} value={count}>{count}</option>)}
                        </select>
                        <span className="text-muted small">entries</span>
                    </div>
                    <div className="dn-search">
                        <i className="bi bi-search"></i>
                        <input type="text" placeholder="Search name, mobile, product..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
                    </div>
                </div>
                <div className="table-responsive">
                    {!listinguid ? (
                        <div className="p-3 text-secondary">Select a business to view its inquiries.</div>
                    ) : loading ? (
                        <div className="p-3">Loading...</div>
                    ) : filteredRows.length === 0 ? (
                        <div className="p-3 text-secondary">No inquiries yet.</div>
                    ) : (
                            <table className="dn-table">
                                <thead className="table-light">
                                    <tr><th>#</th><th>Requester</th><th>Product / Service</th><th>Email</th><th>Message</th><th>Received</th><th>Status</th><th>Reply</th></tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((r, index) => {
                                        const st = STATUS_STYLE[r.status || "pending"];
                                        const isOpen = openReplyId === r.id;
                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr>
                                                    <td className="text-muted">{startIndex + index + 1}</td>
                                                    <td><div className="d-flex align-items-center gap-2"><div className="dn-avatar">{initial(r.name)}</div><div><strong>{r.name}</strong><br /><small className="text-muted"><i className="bi bi-telephone-fill text-success me-1"></i>{r.mobile}</small></div></div></td>
                                                    <td>{r.product ? <span className="badge inquiry-category">{r.product}</span> : <span className="text-secondary">—</span>}</td>
                                                    <td className="small text-secondary">{r.email || <span className="text-secondary">—</span>}</td>
                                                    <td className="small" style={{ maxWidth: 260, whiteSpace: "pre-wrap" }}>{r.message || <span className="text-secondary">—</span>}</td>
                                                    <td className="text-secondary small" style={{ whiteSpace: "nowrap" }}>{r.created_at || "—"}</td>
                                                    <td><span className="badge" style={{ background: st.bg, color: st.color }}>{st.label}</span></td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-primary" onClick={() => toggleReply(r)}>
                                                            {r.admin_reply ? "View / Edit" : "Reply"}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isOpen && (
                                                    <tr>
                                                        <td colSpan={8} style={{ background: "#f9fafc" }}>
                                                            <div className="p-2">
                                                                <textarea
                                                                    className="form-control form-control-sm mb-2"
                                                                    rows={2}
                                                                    placeholder="Write a reply — the customer gets this by email."
                                                                    value={replyDrafts[r.id] ?? ""}
                                                                    onChange={(e) => setReplyDrafts((drafts) => ({ ...drafts, [r.id]: e.target.value }))}
                                                                />
                                                                <div className="d-flex gap-2">
                                                                    <button className="btn btn-sm btn-primary" disabled={sendingId === r.id} onClick={() => sendReply(r, "replied")}>
                                                                        {sendingId === r.id ? "Sending..." : "Send Reply"}
                                                                    </button>
                                                                    <button className="btn btn-sm btn-outline-secondary" disabled={sendingId === r.id} onClick={() => sendReply(r, "closed")}>
                                                                        Mark Closed
                                                                    </button>
                                                                    <button className="btn btn-sm btn-link text-secondary" onClick={() => setOpenReplyId(null)}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                    )}
                </div>
                <div className="dn-foot">
                    <span className="text-muted small">Showing {filteredRows.length ? startIndex + 1 : 0} to {Math.min(startIndex + itemsPerPage, filteredRows.length)} of {filteredRows.length} entries</span>
                    {totalPages > 1 && <div className="dn-pager"><button disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>‹</button>{Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => <button key={page} className={page === currentPage ? "active" : ""} onClick={() => setCurrentPage(page)}>{page}</button>)}<button disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>›</button></div>}
                </div>
            </div>
        </div>
    );
}